import React, { useCallback, useContext, useMemo, useState } from 'react';
import { SelectField as BaseSelectField, ContextMenu, FlexColumn, Table } from '@ubeya/shared-web/components';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';
import useArrayManagement from '@ubeya/shared-web/hooks/useArrayManagement';
import useFormattingMap from '@ubeya/shared-web/hooks/useFormattingMap';
import useAccountConfig from '@ubeya/shared/hooks/account/useAccountConfig';
import { useField } from 'react-final-form';
import styled from 'styled-components';
import privileges from '@ubeya/shared-web/privileges';
import PrivilegesContext from '@ubeya/shared-web/contexts/PrivilegesContext';
import useTranslatedOptions from '@ubeya/shared/hooks/useTranslatedOptions';
import { generatePositionWageValues } from '@ubeya/shared-web/utils/positions';
import SupplierRateModal from './SupplierRateModal';
import { MARK_UP_TYPE_BY_ID, MARK_UP_TYPE_BY_SLUG } from './constants';

const Wrapper = styled(FlexColumn)`
  flex: 1;
  padding: 31px 50px;
  overflow: auto;
`;

const Description = styled.div`
  margin-bottom: 15px;
  font-size: 16px;
  font-weight: 500;
`;

const Container = styled(FlexColumn)`
  flex: 1;
  min-width: 500px;
  .data-grid {
    margin-bottom: 25px;
  }
`;

const SelectField = styled(BaseSelectField)`
  .select {
    margin-bottom: 20px;
    width: 200px;
  }
  .select-label {
    padding: 5px 10px;
  }
  .select-input {
    min-height: 34px;
    padding: 5px 10px;
  }
`;

const ActionRenderer = ({ rowData, rowData: { id }, columnData: { handleEdit } }) => {
  const { t } = useContext(ConfigContext);

  const options = useTranslatedOptions([{ label: t('edit'), onClick: () => handleEdit(rowData), visible: id }]);

  return <ContextMenu options={options} usePortal />;
};

const SupplierRates = () => {
  const { t, isHost } = useContext(ConfigContext);
  const { account } = useAccountConfig();
  const myPrivileges = useContext(PrivilegesContext);

  const hasAmendPayRatePrivilege = !!myPrivileges[privileges.AMEND_PAY_RATE];

  const [editRate, setEditRate] = useState();

  const {
    input: { value: positionsWages, onChange: onChangePositionsWages }
  } = useField('positionsWages');

  const { data: markups, handleAdd: handleAddMarkup, handleEdit: handleEditMarkup } = useArrayManagement('markups');

  const {
    input: { value: baseMarkup, onChange: onChangeBaseMarkup }
  } = useField('markup');

  const {
    input: { value: isClientControlsRates }
  } = useField('isClientControlsRates');

  const {
    input: { value: supplierPositions }
  } = useField('positions');

  const {
    input: { value: markupType }
  } = useField('markupType');

  const {
    input: { value: baseWage, onChange: onChangeBaseWage }
  } = useField('wage');

  const {
    input: { value: basePayrollWage, onChange: onChangeBasePayrollWage }
  } = useField('payrollWage');

  const mappedSupplierPositions = (supplierPositions || []).reduce((output, current) => {
    output[current.id] = { id: current.id, label: t(current.slug) };
    return output;
  }, {});

  const positionsWithRates = useMemo(
    () =>
      (supplierPositions || []).map(({ id }) => {
        const currentPositionWageData = (positionsWages || []).find(({ positionId }) => positionId === id);

        const positionMarkup = (markups || []).find(({ positionId }) => positionId === id);

        const selectedWage = generatePositionWageValues({
          wage: { ...(currentPositionWageData?.wage || { hourlyWage: null, flatWage: null, flatHours: null }) },
          wages: currentPositionWageData?.wages || [],
          externalWage: {
            ...(currentPositionWageData?.payrollWage || { hourlyWage: null, flatWage: null, flatHours: null })
          },
          externalWageKey: 'payrollWage'
        });

        return {
          // FOR TABLE
          wage: selectedWage.wage,
          payrollWage: selectedWage.payrollWage,

          // FOR MODAL
          wages: currentPositionWageData?.wages || [],

          markup: { ...(positionMarkup || { markup: null, positionId: id }) },
          positionId: id
        };
      }),
    [markups, positionsWages, supplierPositions]
  );

  const groupedData = useMemo(() => {
    return [
      { wage: { ...baseWage }, payrollWage: { ...basePayrollWage }, markup: { markup: baseMarkup } },
      ...positionsWithRates
    ];
  }, [baseMarkup, basePayrollWage, baseWage, positionsWithRates]);

  const formattingMap = useFormattingMap();

  const isFlatWage = account?.payrollConfig?.flatIsActive;
  const showFlatWage = isFlatWage && isClientControlsRates;

  const wageFormatter = useCallback(
    ({ value, currency = true }) => (currency ? formattingMap.currency(value) : value),
    [formattingMap]
  );

  const columns = useMemo(
    () => [
      {
        Header: t('position'),
        dataKey: 'positionId',
        formatter: (value) => (value ? mappedSupplierPositions[value]?.label : t('baseRate'))
      },
      {
        Header: t('supplierRatesChargeRate'),
        dataKey: 'wage.hourlyWage',
        formatter: (value) => wageFormatter({ value })
      },
      isClientControlsRates && {
        Header: t('specificClientPayRate'),
        dataKey: 'payrollWage.hourlyWage',
        formatter: (value) => wageFormatter({ value })
      },
      ...(showFlatWage
        ? [
            {
              Header: t('flatWage'),
              dataKey: 'payrollWage.flatWage',
              formatter: (value) => wageFormatter({ value })
            },
            {
              Header: t('flatHours'),
              dataKey: 'payrollWage.flatHours',
              formatter: (value) => wageFormatter({ value, currency: false })
            },
            {
              Header: t('globalWage'),
              dataKey: 'wage.flatWage',
              formatter: (value) => wageFormatter({ value })
            },
            {
              Header: t('flatBillRateHoursLimit'),
              dataKey: 'wage.flatHours',
              formatter: (value) => wageFormatter({ value, currency: false })
            }
          ]
        : []),

      !isHost && {
        Header: t('markup'),
        dataKey: 'markup',
        formatter: ({ markup }) => (markup ? `${MARK_UP_TYPE_BY_ID[markupType] === 'amount' ? '' : '%'} ${markup}` : '')
      }
    ],

    [t, showFlatWage, isHost, mappedSupplierPositions, wageFormatter, markupType, isClientControlsRates]
  );

  const handleAddNewPosition = useCallback(
    ({ positionId, wage = {}, payrollWage = {}, wages = [] }) => {
      const newPositionData = {
        status: 'added',
        positionId,

        // ID INSIDE EACH ITEM IS NOT SEND
        wages,

        // ID IS SENT
        wage: { ...wage, id: -Date.now() },
        payrollWage: { ...payrollWage, id: -Date.now() }
      };
      onChangePositionsWages([...positionsWages, newPositionData]);
    },
    [onChangePositionsWages, positionsWages]
  );

  const handleEditPosition = useCallback(
    ({ positionId, wage, payrollWage, wages = [] }) => {
      onChangePositionsWages(
        positionsWages.map((item) =>
          item.positionId === positionId
            ? {
                ...item,
                status: 'edited',
                wages,
                wage: wage ? { ...item.wage, ...wage } : item.wage,
                payrollWage: payrollWage ? { ...item.payrollWage, ...payrollWage } : item.payrollWage
              }
            : item
        )
      );
    },
    [onChangePositionsWages, positionsWages]
  );

  const onConfirm = useCallback(
    (values) => {
      if (!editRate) {
        return;
      }
      if (!editRate.positionId) {
        const { wage, payrollWage, markup } = values;
        if (wage) {
          onChangeBaseWage(wage);
        }
        if (payrollWage) {
          onChangeBasePayrollWage(payrollWage);
        }

        if (markup?.markup) {
          onChangeBaseMarkup(markup.markup);
        }
        return;
      }

      // PER POSITION RATE
      const matchingPosition = positionsWages.find(
        ({ positionId: currPositionId }) => currPositionId === values.positionId
      );

      const { positionId, wage, payrollWage, wages } = values;

      if (!matchingPosition) {
        handleAddNewPosition({ positionId, wage, payrollWage, wages });
      } else {
        handleEditPosition({ positionId, wage, payrollWage, wages });
      }

      if (editRate.markup.id) {
        handleEditMarkup(editRate.markup.id, values.markup);
      } else {
        handleAddMarkup(values.markup);
      }
    },
    [
      editRate,
      handleAddMarkup,
      handleAddNewPosition,
      handleEditMarkup,
      handleEditPosition,
      onChangeBaseMarkup,
      onChangeBasePayrollWage,
      onChangeBaseWage,
      positionsWages
    ]
  );

  const markupTypesOptions = Object.values(MARK_UP_TYPE_BY_SLUG).map(({ id, label }) => ({
    value: id,
    label: t(label)
  }));

  const disableEditRates = isHost || !hasAmendPayRatePrivilege;

  return (
    <Wrapper>
      <Description>{t('ratesTabDescription')}</Description>

      {hasAmendPayRatePrivilege && (
        <SelectField
          title={t('markupType')}
          name="markupType"
          placeholder={t('markupType')}
          options={markupTypesOptions}
          isMulti={false}
          usePortal
        />
      )}

      <Container>
        <Table
          headerRowHeight={50}
          autoHeight
          columns={columns}
          data={groupedData}
          shadow={false}
          onRowClick={disableEditRates ? null : ({ rowData }) => setEditRate(rowData)}
          actionRenderer={({ rowData }) =>
            !disableEditRates && (
              <ActionRenderer
                rowData={rowData}
                columnData={{
                  handleEdit: setEditRate
                }}
              />
            )
          }
        />
      </Container>

      {editRate && (
        <SupplierRateModal
          isClientControlsRates={isClientControlsRates}
          isFlatWage={showFlatWage}
          selectedRate={editRate}
          mappedPositions={mappedSupplierPositions}
          onClose={() => setEditRate()}
          markupType={markupType}
          onConfirm={onConfirm}
        />
      )}
    </Wrapper>
  );
};

export default React.memo(SupplierRates);
